import type { ManifestObject } from '../content/schema';
import { applyConfidenceTreatment } from './confidenceTier';
import { onMeshClick } from '../selection/onMeshClick';

/**
 * Renders a single ManifestObject as placeholder geometry.
 *
 * Only objects with a `placeholder` block render here — objects with a `model`
 * reference (Phase 4+) will be handled by a separate GLTF renderer.
 *
 * Material choice: `meshStandardMaterial` with roughness=1, metalness=0,
 * flatShading=true. This gives a painterly matte with directional shading
 * without authoring PBR texture maps (AESTHETIC-BRIEF Rule 1 bans PBR texture
 * authoring, not the material class itself). Phase 6 will swap these for
 * baked texture atlases on real .glb assets.
 *
 * Plan 04-02: confidence-tier HSL treatment (PARA-02) is applied via the
 * material ref callback — fires exactly once at material mount. Documented
 * tier is a no-op; inferred and stand-in desaturate + darken (Pitfall #6-safe).
 *
 * Plan 04-03 (PARA-03): `onClick` is attached ONLY to selectable meshes.
 * Scene chrome (ground + walls) is excluded via `SCENE_CHROME_IDS` — Pitfall
 * #10 closure. Attaching onClick to every mesh would make R3F's auto-raycaster
 * test every single one on every pointer move, which tanks framerate.
 *
 * `userData` carries manifest metadata downstream so the raycast selection
 * handler (`onMeshClick`) can read manifestId without a second manifest lookup.
 */

/**
 * Manifest IDs that are scene chrome — NOT selectable, NO onClick.
 *
 * Phase 6 content-fill authors MUST keep this list in sync when they add
 * new non-sourceable scene elements (fog plates, ambient details, etc.).
 */
export const SCENE_CHROME_IDS = new Set<string>([
  'ground',
  'wall-north',
  'wall-south',
  'wall-east',
  'wall-west',
]);

function isSelectable(obj: ManifestObject): boolean {
  return !SCENE_CHROME_IDS.has(obj.id);
}

export function PlaceholderObject({ obj }: { obj: ManifestObject }) {
  if (!obj.placeholder) return null;
  const { geometry, size, color } = obj.placeholder;
  const args: [number, number, number] = size;
  const selectable = isSelectable(obj);

  return (
    <mesh
      position={obj.position}
      rotation={obj.rotation}
      scale={obj.scale}
      userData={{
        manifestId: obj.id,
        confidence: obj.confidence,
        sourceIds: obj.sourceIds,
        era: obj.era,
      }}
      onClick={selectable ? onMeshClick : undefined}
    >
      {geometry === 'box' && <boxGeometry args={args} />}
      {geometry === 'plane' && <planeGeometry args={[args[0], args[2]]} />}
      {geometry === 'cylinder' && (
        <cylinderGeometry args={[args[0] / 2, args[0] / 2, args[1], 24]} />
      )}
      <meshStandardMaterial
        ref={(mat) => {
          if (mat) applyConfidenceTreatment(mat, obj.confidence);
        }}
        color={color}
        roughness={1}
        metalness={0}
        flatShading
      />
    </mesh>
  );
}
